import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CustomerOrder } from '../../../models/customer-order.model';
import { Product } from '../../../models/product.model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { SelectProductToOrderComponent } from '../select-product-to-order/select-product-to-order.component';
import { SharedStateService } from '../../../services/shared-state.service';
import { ModalComponent } from '../../../components/modal/modal.component';
import { CustomerOrderItem } from '../../../models/customer-order-item.model';
import { EmployeeService } from '../../../services/employee.service';
import { Employee } from '../../../models/employee.model';
import { AuthService } from '../../login/auth.service';
import { ProductService } from '../../../services/product.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-create-order',
  imports: [
    InputNumberModule,
    SelectModule,
    CommonModule,
    InputTextModule,
    DatePickerModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    AutoCompleteModule,
    SelectProductToOrderComponent,
    ModalComponent
  ],
  templateUrl: './create-order.component.html'
})
export class CreateOrderComponent implements OnInit {
  @ViewChild('addButton') addButton!: ElementRef;
  @ViewChild('amountInput') amountInput!: InputNumber;
  @ViewChild('productAutoComplete') productAutoComplete!: AutoComplete;
  @Output() onItemAdded = new EventEmitter<CustomerOrder>();

  SUBTRACT_ACTION: string = 'subtract';
  ADD_ACTION: string = 'add';

  form!: FormGroup;
  productId!: number;
  employees: Employee[] = [];
  employeeOptions: { label: string; value: number }[] = [];
  filteredProducts: Product[] = [];
  selectedProduct: Product | null = null;
  order: CustomerOrder = {
    installments: 1,
    installmentValue: 0,
    total: 0,
    downPayment: 0,
    items: [],
    initialInstallmentDate: this.getDefaultFirstChargeDate()
  };
  showModal = false;

  availableInstallments: number[] = [1, 2, 3, 4, 5, 6];

  constructor(
    private fb: FormBuilder,
    private sharedState: SharedStateService,
    private employeeService: EmployeeService,
    private authService: AuthService,
    private productService: ProductService,
    private messageService: MessageService
  ) {}

  private getDefaultFirstChargeDate(): Date {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  }

  get installmentValueLabel(): string {
    const n = Number(this.form?.get('installments')?.value ?? 1);
    const raw = this.form?.get('firstInstallmentValue')?.value;
    const first = raw == null || raw === '' ? null : Number(raw);
    if (n > 1 && first != null && !Number.isNaN(first) && first > 0) {
      return 'Demais parcelas (cada)';
    }
    return 'Valor da parcela';
  }

  @Input() set tabActive(active: boolean) {
    if (active) {
      setTimeout(() => this.focusProductAutoComplete(), 150);
    }
  }

  ngOnInit(): void {
    this.sharedState.selectedItem$.subscribe((selected) => {
      if (selected) {
        this.selectProduct(selected);
        this.sharedState.setSelectedItem(null);
      }
    });

    this.form = this.fb.group({
      productName: [''],
      amount: [null],
      installmentValue: [0],
      totalValue: [0],
      productValue: [0],
      installments: [this.availableInstallments[0]],
      downPayment: [null],
      firstInstallmentValue: [null],
      initialInstallmentDate: [this.getDefaultFirstChargeDate()],
      employeeId: [null, Validators.required]
    });

    this.form.get('initialInstallmentDate')?.valueChanges.subscribe((value: Date | string | null) => {
      this.order.initialInstallmentDate = value ?? undefined;
      this.onItemAdded.emit(this.order);
    });

    this.form.get('employeeId')?.valueChanges.subscribe((value: number | null) => {
      this.order.employeeId = value ?? undefined;
      this.onItemAdded.emit(this.order);
    });

    this.form.get('downPayment')?.valueChanges.subscribe(() => this.recalculateInstallmentSchedule());
    this.form.get('firstInstallmentValue')?.valueChanges.subscribe(() => this.recalculateInstallmentSchedule());

    this.loadEmployeesAndSelectLogged();
    this.recalculateInstallmentSchedule();
  }

  private loadEmployeesAndSelectLogged(): void {
    this.employeeService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.employees = response.data;
          this.employeeOptions = this.employees.map((e: any) => {
            const name = e?.nomeCompleto ?? e?.fullName ?? e?.nome ?? e?.name ?? '';
            return {
              label: name || `Funcionário ${e.id}`,
              value: e.id
            };
          });
          const loggedId = this.authService.getLoggedUserId();
          if (loggedId) {
            const hasEmployee = this.employees.some((e: any) => e.id === loggedId);
            if (hasEmployee) {
              this.form.patchValue({ employeeId: loggedId }, { emitEvent: false });
              this.order.employeeId = loggedId;
              this.onItemAdded.emit(this.order);
            }
          }
        } else {
          this.employees = [];
        }
      },
      error: () => {
        this.employees = [];
      }
    });
  }

  selectProduct = (product: Product) => {
    this.productId = product.id;
    this.selectedProduct = product;
    this.showModal = false;

    this.form.patchValue({
      productName: product.description,
      productValue: product.priceSelling
    });

    this.focusAndSelectAmount();
  };

  searchProducts(event: { query: string }): void {
    this.productService.search(event.query).subscribe({
      next: (response) => {
        this.filteredProducts = response.data ?? [];
      },
      error: () => {
        this.filteredProducts = [];
      }
    });
  }

  onProductSelect(event: any): void {
    const product: Product = event.value ?? event;
    this.productId = product.id;

    this.form.patchValue({
      productName: product.description,
      productValue: product.priceSelling
    });

    this.focusAndSelectAmount();
  }

  private focusAndSelectAmount(): void {
    this.form.patchValue({ amount: 1 });
    setTimeout(() => {
      const inputEl = this.amountInput?.el?.nativeElement?.querySelector('input');
      if (inputEl) {
        inputEl.focus();
        inputEl.select();
      }
    }, 150);
  }

  changeModal() {
    this.showModal = !this.showModal;
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addItem();
    }
  }

  addItem() {
    const amount = this.form.get('amount')?.value;
    if (!amount || amount <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Informe a quantidade antes de adicionar o item.',
        life: 3000
      });
      return;
    }

    const item = this.form.value;

    const currentTotal = this.form.get('totalValue')?.value;
    const installments = this.form.get('installments')?.value;
    const productTotal = item.productValue * item.amount;

    this.calculateTotal(currentTotal, productTotal, installments, this.ADD_ACTION);

    this.order.items.push({
      amount: item.amount,
      productId: this.productId,
      productName: this.selectedProduct?.description ?? item.productName,
      total: productTotal,
      value: item.productValue
    });

    this.order.initialInstallmentDate = this.form.get('initialInstallmentDate')?.value;

    this.productId = 0;
    this.selectedProduct = null;
    this.form.patchValue({
      productName: '',
      productValue: 0,
      amount: null
    });

    this.onItemAdded.emit(this.order);
    this.focusProductAutoComplete();
  }

  private focusProductAutoComplete(): void {
    setTimeout(() => {
      const inputEl = this.productAutoComplete?.el?.nativeElement?.querySelector('input');
      if (inputEl) {
        inputEl.focus();
      }
    });
  }

  deleteItem(item: CustomerOrderItem) {
    const currentTotal = this.form.get('totalValue')?.value;
    const installments = this.form.get('installments')?.value;
    this.calculateTotal(currentTotal, item.total, installments, this.SUBTRACT_ACTION);

    const index: number = this.order.items.indexOf(item);

    if (index > -1) {
      this.order.items.splice(index, 1);
    }

    this.order.initialInstallmentDate = this.form.get('initialInstallmentDate')?.value;
    this.onItemAdded.emit(this.order);
  }

  calculateTotal(currentTotal: number, productValue: number, installments: number, action: string) {
    let total = 0;
    if (action === this.ADD_ACTION) {
      total = currentTotal + productValue;
    } else {
      total = currentTotal - productValue;
    }

    this.order.installments = installments;
    this.form.patchValue(
      {
        totalValue: total,
        installments: installments
      },
      { emitEvent: false }
    );
    this.recalculateInstallmentSchedule();
  }

  calculateInstallments(installment: number) {
    this.form.patchValue({ installments: installment }, { emitEvent: false });
    this.recalculateInstallmentSchedule();
  }

  private roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private distributeEqual(total: number, count: number): number[] {
    const values: number[] = [];
    if (count < 1) {
      return values;
    }
    const base = this.roundMoney(total / count);
    let sum = 0;
    for (let i = 0; i < count - 1; i++) {
      values.push(base);
      sum += base;
    }
    values.push(this.roundMoney(total - sum));
    return values;
  }

  private buildInstallmentValues(financed: number, installments: number, first: number | null): number[] {
    if (installments < 1) {
      return [];
    }
    if (installments === 1) {
      return [financed];
    }
    const hasFirst = first != null && first > 0;
    if (!hasFirst) {
      return this.distributeEqual(financed, installments);
    }
    const f = this.roundMoney(first!);
    const remainder = this.roundMoney(financed - f);
    const others = this.distributeEqual(remainder, installments - 1);
    return [f, ...others];
  }

  private recalculateInstallmentSchedule(): void {
    if (!this.form) {
      return;
    }

    const total = Number(this.form.get('totalValue')?.value ?? 0);
    let downRaw = this.form.get('downPayment')?.value;
    let down =
      downRaw == null || downRaw === '' || Number.isNaN(Number(downRaw)) ? 0 : Number(downRaw);
    if (down < 0) {
      down = 0;
    }
    if (down > total) {
      down = total;
    }

    let firstRaw = this.form.get('firstInstallmentValue')?.value;
    let first: number | null =
      firstRaw == null || firstRaw === '' || Number.isNaN(Number(firstRaw)) ? null : Number(firstRaw);

    const n = Number(this.form.get('installments')?.value ?? 1);
    const financed = this.roundMoney(total - down);

    if (first != null && first < 0) {
      first = 0;
    }
    if (first != null && first > financed) {
      first = financed;
    }

    if (n === 1) {
      first = null;
    }

    const values = this.buildInstallmentValues(financed, n, first);
    const hasCustomFirst = n > 1 && first != null && first > 0;

    const uniformEach = n > 0 ? values[0] : 0;
    let otherEach = 0;
    if (hasCustomFirst && values.length > 1) {
      otherEach = values[1];
    }

    this.order.total = total;
    this.order.installments = n;
    this.order.downPayment = down;
    this.order.firstInstallmentValue = hasCustomFirst ? first! : undefined;
    this.order.installmentValue = hasCustomFirst ? otherEach : uniformEach;
    this.order.otherInstallmentsValue = hasCustomFirst ? otherEach : undefined;
    this.order.initialInstallmentDate = this.form.get('initialInstallmentDate')?.value;

    this.form.patchValue(
      {
        downPayment: down,
        firstInstallmentValue: n === 1 ? null : first === null ? null : first,
        installmentValue: hasCustomFirst ? otherEach : uniformEach
      },
      { emitEvent: false }
    );

    this.onItemAdded.emit(this.order);
  }
}
